import React, { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useDebounceValue } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopFilters.styles';
import { formatRangeValue } from './DesktopFilters.helpers';
import { DesktopFilterAccordion } from './DesktopFilterAccordion';
import { Property } from '@/api/types/categories.types';
import { RangeCollection } from '@/components/Filters/FiltersDrawer.types';
import { Input } from '@/components/UI/Input/Input';
import { EMPTY_RESULTS } from '@/components/Filters/RangeOptions.constants';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

interface Props {
  property: Property;
  collection: RangeCollection;
  onNavigateHref?: (href: string) => void;
}

export const RangeDesktopFilter: FC<Props> = ({
  property,
  collection,
  onNavigateHref,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const { legacyResourceId, measurementUnits, name, filterOpenByDefault } =
    property;
  const [selectedFrom, selectedTo] =
    collection[legacyResourceId] || EMPTY_RESULTS;
  const selectedValues = {
    from: typeof selectedFrom === 'number' ? selectedFrom.toString() : '',
    to: typeof selectedTo === 'number' ? selectedTo.toString() : '',
  };
  const [values, setValues] = useState<{
    from: string;
    to: string;
  }>(selectedValues);
  const [debouncedValues] = useDebounceValue(values, 1_000);
  const accordionRef = useRef<AccordionRef>(null);

  const formattedValue = formatRangeValue(
    { from: selectedFrom?.toString(), to: selectedTo?.toString() },
    measurementUnits ?? '',
    t
  );
  const shouldOpenByDefault = !!filterOpenByDefault && !formattedValue;

  function isFocusedOnInput() {
    if (typeof window === 'undefined') return false;

    const activeId = document.activeElement?.id;
    const inputsIds = [
      `${legacyResourceId}-input-from`,
      `${legacyResourceId}-input-to`,
    ];
    return activeId && inputsIds.includes(activeId);
  }

  function applyNewValues(newValues: typeof values) {
    if (
      newValues.from === selectedValues.from &&
      newValues.to === selectedValues.to
    ) {
      return;
    }
    const searchParams = new URLSearchParams(queryString);
    const key = `p[${legacyResourceId}][]`;
    searchParams.delete(key);
    // `user_id` is preserved so desktop filters keep the user ads page scoped.

    if (newValues.from || newValues.to) {
      searchParams.append(key, newValues.from);
      searchParams.append(key, newValues.to);
    }
    searchParams.set(QUERY.keywordsSource, KeywordsSource.Typewritten);

    const href = searchParams.size
      ? `${clearUrl}?${searchParams.toString()}`
      : clearUrl;

    if (onNavigateHref) {
      onNavigateHref(href);
      return;
    }

    router.push(href, undefined, { scroll: false });

    if (newValues.from || newValues.to) {
      accordionRef.current?.close();
    }
  }

  function handleChange(value: string | null, fieldName: keyof typeof values) {
    setValues({ ...values, [fieldName]: value });
  }

  function handleClear() {
    setValues({ from: '', to: '' });
  }

  useEffect(() => {
    applyNewValues(debouncedValues);
  }, [debouncedValues]);

  useEffect(() => {
    if (
      !isFocusedOnInput() &&
      (selectedFrom !== values.from || selectedTo !== values.to)
    ) {
      setValues(selectedValues);
    }
  }, [selectedFrom, selectedTo]);

  return (
    <DesktopFilterAccordion
      key={`${legacyResourceId}-${String(shouldOpenByDefault)}`}
      ref={accordionRef}
      title={name}
      value={formattedValue}
      onClear={handleClear}
      dataTestId={`range-property-${legacyResourceId}`}
      defaultOpen={shouldOpenByDefault}
    >
      <S.RangeFieldsContainer>
        <Input
          id={`${legacyResourceId}-input-from`}
          isNumber
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={values.from?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'from')}
          type="search"
          appearance="filled"
          placeholder={t('common.from')}
          label={t('common.from')}
          labelPosition="left"
          customPaddingTop={2}
          data-testid="range-from"
        />
        <Input
          id={`${legacyResourceId}-input-to`}
          isNumber
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={values.to?.toString() ?? ''}
          onChange={value => handleChange(value as string, 'to')}
          type="search"
          appearance="filled"
          placeholder={t('common.to')}
          label={t('common.to')}
          labelPosition="left"
          customPaddingTop={2}
          data-testid="range-to"
        />
      </S.RangeFieldsContainer>
    </DesktopFilterAccordion>
  );
};
